<template>
  <div :class="boxClass">
    <VTooltip theme="baseTooltip">
      <button
        v-if="isSignedIn"
        type="button"
        :class="buttonClass"
        :aria-label="t('add_to_favorite')"
        aria-pressed="false"
        :title="t('add_to_favorite')"
        @click="toggleFavourite"
      >
        <i
          v-if="appStore.isFavorite(id)"
          class="mi-heart-fill text-danger icon-20"
        />
        <i
          v-else
          class="mi-heart icon-20"
          :class="iconColorClass"
        />
        <div
          v-if="appStore.isFavorite(id) && showHeartAnimation"
          class="heart-pop events-none"
        >
          <i
            v-for="item in 5"
            :key="item"
            class="mi-heart-fill text-danger icon-20"
          />
        </div>
        <div
          v-if="appStore.isFavorite(id) && showHeartAnimation"
          class="added-to-favourite px-3 py-2 bg-white rounded-lg shadow absolute text-primary"
        >
          {{ t('added_to_favorite') }}
        </div>
      </button>
      <button
        v-else
        :class="buttonClass"
        aria-label="Add to favorite"
        type="button"
        @click="openModal({ id: 'modal-sign-in' })"
      >
        <i
          class="mi-heart icon-20"
          :class="iconColorClass"
        />
      </button>
      <template #popper>
        {{ t('add_to_favorite') }}
      </template>
    </VTooltip>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAppStore } from '@/stores/AppStore'
import { useModalStore } from '@/stores/ModalStore'

const props = defineProps({
  id: {
    type: [Number, String],
    required: true,
  },
  btnClass: {
    type: String,
    default: '',
  },
  iconSize: {
    type: [Number, String],
    default: 30,
  },
  iconColorClass: {
    type: String,
    default: 'text-muted',
  },
  boxClass: {
    type: String,
    default: 'm-1 md:m-2 lg:m-4',
  },
})

const { openModal } = useModalStore()

const { t } = useI18n()

const appStore = useAppStore()

const { isSignedIn } = storeToRefs(appStore)

const showHeartAnimation = ref(false)
const resetFavouriteShow = setTimeout(() => {
  showHeartAnimation.value = false
}, 4000)
const toggleFavourite = () => {
  clearTimeout(resetFavouriteShow)
  appStore.toggleFavoriteProducts(props.id)
  showHeartAnimation.value = true
}

const buttonClass = ['btn p-1 rounded-circle relative', props.btnClass]
</script>

<i18n>
  {
    "cs": {
    "add_to_favorite": "Přidat do oblíbených",
    "added_to_favorite": "Přidáno do oblíbených"
},
    "sk": {
    "add_to_favorite": "Pridať do obľúbených",
    "added_to_favorite": "Pridané do obľúbených"
},
    "pl": {
    "add_to_favorite": "Dodaj do ulubionych",
    "added_to_favorite": "Dodano do ulubionych"
},
    "hu": {
    "add_to_favorite": "Hozzáadás a kedvencekhez",
    "added_to_favorite": "Hozzáadva a kedvencekhez"
},
    "de": {
    "add_to_favorite": "Zu Favoriten hinzufügen",
    "added_to_favorite": "Zu den Favoriten hinzugefügt"
},
    "at": {
    "add_to_favorite": "Zu Favoriten hinzufügen",
    "added_to_favorite": "Zu den Favoriten hinzugefügt"
},
    "ro": {
    "add_to_favorite": "Adăugați la favorite",
    "added_to_favorite": "Adăugat la favorite"
},
    "fr": {
    "add_to_favorite": "Ajouter aux favoris",
    "added_to_favorite": "Ajouté aux favoris"
},
    "it": {
    "add_to_favorite": "Aggiungi ai preferiti",
    "added_to_favorite": "Aggiunto ai preferiti"
}
  }
</i18n>

<style lang="scss">
@keyframes favouriteAdded {
  0% {
    transform: translate(-80%, -50%);
    opacity: 0;
  }
  20% {
    transform: translate(-100%, -50%);
    opacity: 1;
  }
  80% {
    transform: translate(-100%, -50%);
    opacity: 1;
  }
  100% {
    transform: translate(-120%, -50%);
    opacity: 0;
  }
}
.added-to-favourite {
  left: 0%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: 2s favouriteAdded forwards ease-out;
}
.heart-pop {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;

  @keyframes heartMove1 {
    from {
      transform: translate(-50%, -50%);
      opacity: 1;
    }
    to {
      transform: translate(100%, 100%);
      opacity: 0;
    }
  }
  @keyframes heartMove2 {
    from {
      transform: translate(-50%, -50%);
      opacity: 1;
    }
    to {
      transform: translate(-150%, -150%);
      opacity: 0;
    }
  }
  @keyframes heartMove3 {
    from {
      transform: translate(-50%, -50%);
      opacity: 1;
    }
    to {
      transform: translate(-150%, 180%);
      opacity: 0;
    }
  }
  @keyframes heartMove4 {
    from {
      transform: translate(-50%, -50%);
      opacity: 1;
    }
    to {
      transform: translate(150%, -100%);
      opacity: 0;
    }
  }
  @keyframes heartMove5 {
    from {
      transform: translate(-50%, -50%);
      opacity: 1;
    }
    to {
      transform: translate(-50%, -150%);
      opacity: 0;
    }
  }

  i {
    position: absolute;
    width: 20px;
    height: auto;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    &:nth-child(1) {
      animation: 0.5s heartMove1 0.1s forwards ease-out;
    }
    &:nth-child(2) {
      animation: 0.5s heartMove2 0.2s forwards ease-out;
    }
    &:nth-child(3) {
      animation: 0.5s heartMove3 0.3s forwards ease-out;
    }
    &:nth-child(4) {
      animation: 0.5s heartMove4 0.2s forwards ease-out;
    }
    &:nth-child(5) {
      animation: 0.5s heartMove5 0.1s forwards ease-out;
    }
  }
}
</style>
